import Components from "slotworks/components/components";
import graphicsManager from "slotworks/graphics/graphics-manager";
import SlotDefinition from "slotworks/model/slot-definition";
import Logger from "slotworks/utils/logger";
import SpriteMapPool from "slotworks/utils/sprite-pool";

export class SymbolStaticPool {

    public static NUMBER_OF_SYMBOLS: number;

    private staticSpriteMapPool = new SpriteMapPool();
    private staticSpritesBlurredMapPool = new SpriteMapPool();

    public initialise(matrixGrid: number[], reelsets: Map<string, string[][]>, extendedReelSpinner: boolean) {

        const numberOfSymbols = SymbolStaticPool.NUMBER_OF_SYMBOLS || this.estimateMaxSymbols(matrixGrid, reelsets, extendedReelSpinner);

        for (let i = 0; i < numberOfSymbols; i++) {
            this.setupStaticSymbolGraphics();
        }

        this.report();
    }

    public getStaticSpriteMap() {
        return this.getFromPoolOrCreate(this.staticSpriteMapPool);
    }

    public getStaticBlurredSpriteMap() {
        return this.getFromPoolOrCreate(this.staticSpritesBlurredMapPool);
    }

    public releaseStaticSprites(spriteMap: Map<string, PIXI.Sprite>) {
        this.staticSpriteMapPool.release(spriteMap);
    }

    public releaseStaticBlurredSprites(spriteMap: Map<string, PIXI.Sprite>) {
        this.staticSpritesBlurredMapPool.release(spriteMap);
    }

    /**
     * Estimates the games maxiumum needed symbols.
     *
     * The most reliable estimate is available when the game's matrix doesn't change size. In the case it does resize,
     * a warning will be issued.
     *
     * If these estimates are wrong and too few symbols are initialised, more will be generated at runtime and an
     * warning issued.
     *
     * @param matrixGrid
     * @param reelsets
     * @param extendedReelSpinner
     */
    private estimateMaxSymbols(matrixGrid: number[], reelsets: Map<string, string[][]>, extendedReelSpinner: boolean) {

        const largestReelSet = this.getLargestReelset(reelsets);

        if (largestReelSet.length !== matrixGrid.length) {
            Logger.warn("Unable to reliably estimate number of symbols to pool, the matrixGrid size doesn't match all reelsets.");
            Logger.warn("Please manually provide the number of symbols to pool: SymbolStaticPool.NUMBER_OF_SYMBOLS = x;");
        }

        Logger.info("Populating symbol pool based on matrix grid definition.");
        return this.estimateNumberOfSymbolsUsingGridSize(matrixGrid, extendedReelSpinner);
    }

    /**
     * Estimates the number of symbols needed to populate the reels using the matrix size specified in the game's config.
     *
     * Accounts for novel shaped matrixes: i.e: [1,2,3,2,1].
     *
     * @param matrixGrid
     * @param extendedReelSpinner
     */
    private estimateNumberOfSymbolsUsingGridSize(matrixGrid: number[], extendedReelSpinner: boolean) {
        const extraSymbolsPerReel = this.getExtraSymbolsPerReel(extendedReelSpinner);

        let currentTotal = 0;

        matrixGrid.forEach((reelSize) => {
            return currentTotal += reelSize + extraSymbolsPerReel;
        });

        return currentTotal;
    }

    private getExtraSymbolsPerReel(extendedReelSpinner: boolean) {
        return extendedReelSpinner ? 3 : 1;
    }

    private getLargestReelset(reelsets: Map<string, string[][]>) {
        const defaultReelset = Components.config.matrix.defaultReelset;
        let largestReelset = SlotDefinition.reelsets.get(defaultReelset);

        reelsets.forEach((reelset) => {
            if (reelset.length > largestReelset.length) {
                largestReelset = reelset;
            }
        });

        return largestReelset;
    }

    private getFromPoolOrCreate(spritePool: SpriteMapPool) {
        const spriteMap = spritePool.get();

        if (spriteMap) {
            return spriteMap;
        }

        this.setupStaticSymbolGraphics();

        Logger.warn("Creating static symbol maps at run time. New pool size: %s.", spritePool.getSize());
        return spritePool.get();
    }

    private setupStaticSymbolGraphics() {
        const staticSprites = this.createStaticSpriteForSymbols();
        this.staticSpriteMapPool.add(staticSprites);

        const staticSpritesBlurred = this.createStaticBlurredSpriteForSymbols();
        this.staticSpritesBlurredMapPool.add(staticSpritesBlurred);
    }

    private createStaticBlurredSpriteForSymbols() {
        const staticSpritesBlurred = new Map<string, PIXI.Sprite>();

        SlotDefinition.symbolDefinitions.forEach((symbolDef) => {
            const blurredSprite = this.getBlurredSprite(symbolDef.id);

            if (blurredSprite) {
                staticSpritesBlurred.set(symbolDef.id, blurredSprite);
            }
        });

        return staticSpritesBlurred;
    }

    private createStaticSpriteForSymbols() {
        const staticSprites = new Map<string, PIXI.Sprite>();

        SlotDefinition.symbolDefinitions.forEach((symbolDef) => {
            staticSprites.set(symbolDef.id, this.getSprite(symbolDef.id));
        });

        return staticSprites;
    }

    private getSprite(symbolId: string) {
        const sprite = graphicsManager.createSprite(symbolId);

        sprite.anchor.set(0.5);
        sprite.visible = false;

        return sprite;
    }

    private getBlurredSprite(symbolId: string) {

        if (!graphicsManager.hasTexture(symbolId + "_blur")) {
            return null;
        }

        const staticSpriteBlurred = graphicsManager.createSprite(symbolId + "_blur");
        staticSpriteBlurred.anchor.set(0.5);
        staticSpriteBlurred.visible = false;

        return staticSpriteBlurred;
    }

    private report() {
        Logger.info("Pooling %s static symbols", this.staticSpriteMapPool.getSize());
        Logger.info("Pooling %s blurred static symbols", this.staticSpritesBlurredMapPool.getSize());
    }
}

export default new SymbolStaticPool();
